import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import TabNavigation from "./ui/TabNavigation";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  SparklesIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  CpuChipIcon,
  LightBulbIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon as TrendingUpIcon,
  BeakerIcon,
  DocumentMagnifyingGlassIcon,
  BoltIcon,
  EyeIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

const AdvancedAI = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboardData, setDashboardData] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [analysisResults, setAnalysisResults] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [trendingInsights, setTrendingInsights] = useState([]);
  const [modelPerformance, setModelPerformance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const { token } = useAuth();

  // Fetch data on component mount
  useEffect(() => {
    const fetchAIData = async () => {
      if (!token) return;
      
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        // Fetch AI dashboard data
        const dashboardRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ai/dashboard`, { headers });
        setDashboardData(dashboardRes.data);
        
        // Fetch compliance predictions
        const predictionsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ai/predictions/compliance`, { headers });
        setPredictions(predictionsRes.data);
        
        // Fetch trending insights
        const trendingRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ai/insights/trending`, { headers });
        setTrendingInsights(trendingRes.data);
        
        // Fetch model performance
        const performanceRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ai/models/performance`, { headers });
        setModelPerformance(performanceRes.data);
        
      } catch (error) {
        console.error('Error fetching AI data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAIData();
  }, [token]);

  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = chatInput.trim();
    setChatInput("");
    setChatLoading(true);
    
    // Add user message to chat
    setChatMessages(prev => [...prev, { type: "user", content: userMessage, timestamp: new Date() }]);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
        query: userMessage,
        context: {}
      }, { headers });
      
      // Add AI response to chat
      setChatMessages(prev => [...prev, { 
        type: "ai", 
        content: response.data.response, 
        timestamp: new Date(),
        relatedDocs: response.data.related_documents,
        suggestedActions: response.data.suggested_actions,
        followUpQuestions: response.data.follow_up_questions
      }]);
      
    } catch (error) {
      console.error('Error sending chat message:', error);
      setChatMessages(prev => [...prev, { 
        type: "ai", 
        content: "I'm sorry, I encountered an error processing your request. Please try again.", 
        timestamp: new Date()
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const performAnalysis = async (analysisType, inputData = {}) => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/ai/analyze`, {
        analysis_type: analysisType,
        input_data: inputData
      }, { headers });
      
      setAnalysisResults(response.data);
      
    } catch (error) {
      console.error('Error performing analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case "alert":
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />;
      case "prediction":
        return <TrendingUpIcon className="w-5 h-5 text-blue-600" />;
      case "optimization":
        return <LightBulbIcon className="w-5 h-5 text-yellow-600" />;
      case "recommendation":
        return <SparklesIcon className="w-5 h-5 text-purple-600" />;
      default:
        return <CpuChipIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  const getConfidenceColor = (level) => {
    switch (level) {
      case "high":
        return "bg-green-100 text-green-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString() + " " + new Date(dateString).toLocaleTimeString();
  };

  const tabs = [
    { id: "dashboard", name: "AI Dashboard", icon: ChartBarIcon },
    { id: "chat", name: "AI Assistant", icon: ChatBubbleLeftRightIcon },
    { id: "analysis", name: "Smart Analysis", icon: BeakerIcon },
    { id: "predictions", name: "Predictions", icon: TrendingUpIcon },
    { id: "insights", name: "Trending Insights", icon: EyeIcon },
    { id: "models", name: "Model Performance", icon: CpuChipIcon },
  ];

  const renderDashboardTab = () => (
    <div className="space-y-8">
      {/* Overview Metrics */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {Object.entries(dashboardData.overview).map(([key, value], index) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600 mb-2">
                  {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>
                <p className="text-3xl font-bold text-gray-900">{value}</p>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* AI Insights */}
      {dashboardData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center space-x-2 mb-6">
            <SparklesIcon className="w-6 h-6 text-purple-600" />
            <h3 className="text-xl font-bold text-gray-900">AI-Powered Insights</h3>
          </div>
          
          <div className="space-y-4">
            {dashboardData.insights.map((insight, index) => (
              <motion.div
                key={insight.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    {getInsightIcon(insight.insight_type)}
                    <h4 className="font-semibold text-gray-900">{insight.title}</h4>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getConfidenceColor(insight.confidence_level)}`}>
                      {insight.confidence_level.toUpperCase()}
                    </span>
                    <span className="text-sm text-gray-600">{(insight.confidence_score * 100).toFixed(0)}%</span>
                  </div>
                </div>
                
                <p className="text-gray-700 mb-3">{insight.description}</p>
                
                {insight.supporting_evidence.length > 0 && (
                  <div className="mb-3">
                    <span className="text-sm font-medium text-gray-700">Evidence:</span>
                    <ul className="mt-1 space-y-1">
                      {insight.supporting_evidence.map((evidence, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start space-x-2">
                          <span className="text-blue-600">•</span>
                          <span>{evidence}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {insight.recommended_actions.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Recommended Actions:</span>
                    <ul className="mt-1 space-y-1">
                      {insight.recommended_actions.map((action, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start space-x-2">
                          <span className="text-green-600">✓</span>
                          <span>{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* AI Capabilities */}
      {dashboardData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h3 className="text-xl font-bold text-gray-900 mb-6">AI Capabilities</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(dashboardData.ai_capabilities).map(([capability, enabled]) => (
              <div key={capability} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className={`w-3 h-3 rounded-full ${enabled ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                <span className="text-sm font-medium text-gray-900">
                  {capability.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );

  const renderChatTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6 h-96 flex flex-col">
        <div className="flex items-center space-x-2 mb-4">
          <ChatBubbleLeftRightIcon className="w-6 h-6 text-blue-600" />
          <h3 className="text-xl font-bold text-gray-900">AI Compliance Assistant</h3>
        </div>
        
        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto space-y-4 mb-4">
          {chatMessages.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              <ChatBubbleLeftRightIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p>Ask me anything about compliance, security frameworks, or risk management!</p>
              <div className="mt-4 space-y-2">
                <p className="text-sm text-gray-400">Try asking:</p>
                <div className="space-y-1 text-sm text-blue-600">
                  <p>"What are the key requirements of GDPR?"</p>
                  <p>"How do I implement ISO 27001 controls?"</p>
                  <p>"What is the difference between inherent and residual risk?"</p>
                </div>
              </div>
            </div>
          )}
          
          {chatMessages.map((message, index) => (
            <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                message.type === 'user' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-900'
              }`}>
                <p className="text-sm">{message.content}</p>
                
                {message.type === 'ai' && message.relatedDocs && message.relatedDocs.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs font-medium text-gray-600 mb-1">Related Documents:</p>
                    <div className="space-y-1">
                      {message.relatedDocs.map((doc, idx) => (
                        <div key={idx} className="text-xs text-blue-600 hover:underline cursor-pointer">
                          {doc}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {message.type === 'ai' && message.followUpQuestions && message.followUpQuestions.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs font-medium text-gray-600 mb-1">Follow-up Questions:</p>
                    <div className="space-y-1">
                      {message.followUpQuestions.slice(0, 2).map((question, idx) => (
                        <button
                          key={idx}
                          onClick={() => setChatInput(question)}
                          className="block text-xs text-blue-600 hover:underline cursor-pointer text-left"
                        >
                          {question}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {chatLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 text-gray-900 px-4 py-2 rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm">AI is thinking...</span>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Chat Input */}
        <div className="flex space-x-2">
          <input
            type="text"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
            placeholder="Ask your compliance question..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={chatLoading}
          />
          <button
            onClick={sendChatMessage}
            disabled={!chatInput.trim() || chatLoading}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );

  const renderAnalysisTab = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900">Smart AI Analysis</h3>
      
      {/* Analysis Options */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <div className="text-center">
            <DocumentMagnifyingGlassIcon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h4 className="font-semibold text-gray-900 mb-2">Compliance Gap Analysis</h4>
            <p className="text-sm text-gray-600 mb-4">AI-powered analysis of compliance gaps across frameworks</p>
            <button
              onClick={() => performAnalysis('compliance_gap', { frameworks: ['iso_27001', 'soc2'] })}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Analyze Gaps
            </button>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <div className="text-center">
            <TrendingUpIcon className="w-12 h-12 text-green-600 mx-auto mb-4" />
            <h4 className="font-semibold text-gray-900 mb-2">Risk Prediction</h4>
            <p className="text-sm text-gray-600 mb-4">Predict future risk trends using machine learning</p>
            <button
              onClick={() => performAnalysis('risk_prediction', { categories: ['cybersecurity', 'operational'] })}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              Predict Risks
            </button>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow p-6"
        >
          <div className="text-center">
            <CpuChipIcon className="w-12 h-12 text-purple-600 mx-auto mb-4" />
            <h4 className="font-semibold text-gray-900 mb-2">Document Classification</h4>
            <p className="text-sm text-gray-600 mb-4">Automatically classify and categorize documents</p>
            <button
              onClick={() => performAnalysis('document_analysis', { 
                documents: [
                  { id: 'doc1', name: 'Access Control Policy', content: 'This policy defines access control requirements for authentication and authorization of users...' },
                  { id: 'doc2', name: 'Privacy Notice', content: 'This notice explains how we collect and process personal data in accordance with GDPR...' }
                ]
              })}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Classify Docs
            </button>
          </div>
        </motion.div>
      </div>
      
      {/* Analysis Results */}
      {analysisResults && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Analysis Results</h4>
          
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">{analysisResults.summary}</p>
            <p className="text-xs text-blue-600 mt-1">
              Confidence: {(analysisResults.confidence_score * 100).toFixed(0)}% | 
              Processing Time: {analysisResults.processing_time_ms.toFixed(0)}ms
            </p>
          </div>
          
          <div className="space-y-4">
            {analysisResults.insights.map((insight, index) => (
              <div key={insight.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-2">
                  <h5 className="font-medium text-gray-900">{insight.title}</h5>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getConfidenceColor(insight.confidence_level)}`}>
                    {(insight.confidence_score * 100).toFixed(0)}%
                  </span>
                </div>
                <p className="text-sm text-gray-700 mb-3">{insight.description}</p>
                
                {insight.recommended_actions.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-gray-700">Recommendations:</span>
                    <ul className="mt-1 space-y-1">
                      {insight.recommended_actions.map((action, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start space-x-2">
                          <span className="text-green-600">→</span>
                          <span>{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );

  const renderPredictionsTab = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900">AI Predictions & Forecasting</h3>
      
      {predictions && (
        <>
          {/* Compliance Forecast */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Compliance Score Forecast</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="text-center">
                <p className="text-sm text-gray-600">Current Score</p>
                <p className="text-3xl font-bold text-blue-600">{predictions.compliance_forecast.current_overall_score}%</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">6 Months</p>
                <p className="text-3xl font-bold text-green-600">{predictions.compliance_forecast.predicted_6_months}%</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">12 Months</p>
                <p className="text-3xl font-bold text-green-600">{predictions.compliance_forecast.predicted_12_months}%</p>
              </div>
            </div>
            
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-sm text-green-800">
                Confidence: {(predictions.compliance_forecast.confidence * 100).toFixed(0)}%
              </p>
            </div>
          </motion.div>
          
          {/* Timeline Milestones */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Predicted Milestones</h4>
            <div className="space-y-4">
              {predictions.timeline_milestones.map((milestone, index) => (
                <div key={index} className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                  <ClockIcon className="w-5 h-5 text-blue-600" />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{milestone.milestone}</p>
                    <p className="text-sm text-gray-600">{formatDate(milestone.date)}</p>
                  </div>
                  <span className="text-sm font-medium text-green-600">{milestone.impact}</span>
                </div>
              ))}
            </div>
          </motion.div>
          
          {/* Framework Predictions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Framework-Specific Predictions</h4>
            <div className="space-y-4">
              {predictions.framework_predictions.map((framework, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-medium text-gray-900">{framework.framework.replace('_', ' ').toUpperCase()}</h5>
                    <span className="text-sm text-blue-600">{framework.improvement_rate}</span>
                  </div>
                  <div className="flex items-center space-x-4 mb-3">
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Current</p>
                      <p className="text-lg font-bold text-gray-900">{framework.current_score}%</p>
                    </div>
                    <TrendingUpIcon className="w-5 h-5 text-green-600" />
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Predicted</p>
                      <p className="text-lg font-bold text-green-600">{framework.predicted_score}%</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Key Drivers:</p>
                    <ul className="space-y-1">
                      {framework.key_drivers.slice(0, 2).map((driver, idx) => (
                        <li key={idx} className="text-xs text-gray-600">• {driver}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </div>
  );

  const renderInsightsTab = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900">Trending AI Insights</h3>
      
      <div className="space-y-4">
        {trendingInsights.map((insight, index) => (
          <motion.div
            key={insight.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow p-6"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                <BoltIcon className="w-5 h-5 text-yellow-600" />
                <h4 className="font-semibold text-gray-900">{insight.title}</h4>
              </div>
              <div className="text-right">
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                  {insight.type.replace('_', ' ').toUpperCase()}
                </span>
                <p className="text-xs text-gray-500 mt-1">{(insight.confidence * 100).toFixed(0)}% confidence</p>
              </div>
            </div>
            
            <p className="text-gray-700 mb-3">{insight.description}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div>
                <span className="text-sm font-medium text-gray-700">Affected Controls:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {insight.affected_controls.map((control, idx) => (
                    <span key={idx} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                      {control}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-700">First Detected:</span>
                <p className="text-sm text-gray-600 mt-1">{formatDate(insight.first_detected)}</p>
              </div>
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg">
              <span className="text-sm font-medium text-blue-800">Recommended Response:</span>
              <p className="text-sm text-blue-700 mt-1">{insight.recommended_response}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const renderModelsTab = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900">AI Model Performance</h3>
      
      {modelPerformance && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(modelPerformance.models).map(([modelName, metrics], index) => (
              <motion.div
                key={modelName}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg shadow p-6"
              >
                <h4 className="font-semibold text-gray-900 mb-4">
                  {modelName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </h4>
                
                <div className="space-y-3">
                  {Object.entries(metrics).map(([metric, value]) => {
                    if (typeof value === 'string') return null;
                    
                    return (
                      <div key={metric} className="flex justify-between">
                        <span className="text-sm text-gray-600">
                          {metric.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {typeof value === 'number' && value < 1 && value > 0 
                            ? (value * 100).toFixed(1) + '%'
                            : typeof value === 'number'
                            ? value.toFixed(2)
                            : value
                          }
                        </span>
                      </div>
                    );
                  })}
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500">
                    Last Updated: {formatDate(metrics.last_trained || metrics.last_updated)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Performance Recommendations</h4>
            <div className="space-y-3">
              {modelPerformance.recommendations.map((recommendation, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-yellow-50 rounded-lg">
                  <LightBulbIcon className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <p className="text-sm text-yellow-800">{recommendation}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </div>
  );

  return (
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl font-bold text-gray-900">Advanced AI Features</h1>
            <p className="text-gray-600 mt-2">
              Harness the power of artificial intelligence for compliance analysis, risk prediction, and intelligent automation
            </p>
          </motion.div>
        </div>

        {/* Tab Navigation */}
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tab Content */}
        <div className="tab-content">
          {loading && activeTab !== 'chat' ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {activeTab === "dashboard" && renderDashboardTab()}
              {activeTab === "chat" && renderChatTab()}
              {activeTab === "analysis" && renderAnalysisTab()}
              {activeTab === "predictions" && renderPredictionsTab()}
              {activeTab === "insights" && renderInsightsTab()}
              {activeTab === "models" && renderModelsTab()}
            </>
          )}
        </div>
      </div>
  );
};

export default AdvancedAI;